<script setup lang="ts">
    import { inject, onMounted, reactive } from 'vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { FeatureService } from '@shared/directives/Feature.service';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import Loading from '@shared/components/Loading.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import ConfirmButton from '@shared/components/ConfirmButton.vue';
    import List from '@shared/components/List/List.vue';
    import AddCreditCard from '@cwd/Account/CreditCards/AddCreditCard.vue';
    import ChargebeeCreditCards from '@cwd/Account/CreditCards/ChargebeeCreditCards.vue';
    import { formatDate, formatCustomDatePattern } from '@shared/utils/formatDateTime';
    import {
        CreditCardReference,
        ChargebeePaymentSourcesInfo,
        getCreditCards,
        getChargebeePaymentSources,
        setDefaultCreditCard,
        deleteCreditCard,
    } from '@cwd/Account/CreditCards/CreditCards.access';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);

    const state = reactive<{
        isLoading: boolean;
        loadError: boolean;
        creditCards: CreditCardReference[];
        chargebee: ChargebeePaymentSourcesInfo | null;
    }>({
        isLoading: true,
        loadError: false,
        creditCards: [],
        chargebee: null,
    });

    onMounted(async () => {
        try {
            // Chargebee-owned: the cards live in Chargebee, so its card section replaces the Swedbank list.
            // A failed check falls back to that list: its Add refuses a Chargebee-owned account.
            state.chargebee = await getChargebeePaymentSources().catch(() => null);
            if (state.chargebee?.ChargebeeOwned) return;

            await loadCreditCards();
        } catch {
            state.loadError = true;
        } finally {
            state.isLoading = false;
        }
    });

    async function loadCreditCards(): Promise<void> {
        state.creditCards = await getCreditCards();
    }

    function creditCardBrandClass(creditCard: CreditCardReference): string {
        if (creditCard.CardBrand === 'MasterCard') return 'mastercard';
        if (creditCard.CardBrand === 'Visa') return 'visa';
        return '';
    }

    function canMakeDefault(creditCard: CreditCardReference): boolean {
        return creditCard.UserId === contextAccess.state.currentUser?.UserId && !creditCard.IsDefault;
    }

    function canDelete(creditCard: CreditCardReference): boolean {
        return canMakeDefault(creditCard) || featureAccess.hasBackOfficeAccess();
    }

    async function makeDefault(creditCard: CreditCardReference): Promise<void> {
        await setDefaultCreditCard(creditCard.CreditCardId)
            .then(async () => {
                bsAlert({});
                await loadCreditCards();
            })
            .catch(() => bsAlert({ title: $Resources.ErrorHeading, content: $Resources.ErrorContactSupport, type: AlertTypes.Danger }));
    }

    async function remove(creditCard: CreditCardReference): Promise<void> {
        await deleteCreditCard(creditCard.CreditCardId)
            .then(async () => {
                bsAlert({});
                await loadCreditCards();
            })
            .catch(() => bsAlert({ title: $Resources.ErrorHeading, content: $Resources.ErrorContactSupport, type: AlertTypes.Danger }));
    }
</script>

<template>
    <h1 class="h2 m-lg-b">{{ $Resources.CreditCardsHeading }}</h1>

    <Loading :show="state.isLoading" loadingStyle="progress-bar" fixed></Loading>

    <BsAlert v-if="state.loadError" :type="AlertTypes.Danger" :dismissible="false" :title="$Resources.ErrorHeading" :content="$Resources.ErrorContactSupport" />

    <ChargebeeCreditCards
        v-if="!state.isLoading && !state.loadError && state.chargebee?.ChargebeeOwned"
        :site="state.chargebee.Site!"
        :currentAccount="contextAccess.state.currentAccount"></ChargebeeCreditCards>

    <template v-else-if="!state.isLoading && !state.loadError">
        <AddCreditCard :currentAccount="contextAccess.state.currentAccount"></AddCreditCard>

        <BsAlert :type="AlertTypes.Info" v-if="state.creditCards.length > 0" :dismissible="false" :content="$Resources.CreditCardsIntroduction"></BsAlert>

        <List
            itemId="CreditCardId"
            displayAs="table"
            :items="state.creditCards"
            :tableHeaders="[
                { name: '', classes: 'autofit' },
                $Resources.CreditCardMaskLabel,
                $Resources.CreditCardExpiresLabel,
                $Resources.CreditCardTypeLabel,
                $Resources.CreditCardOwnerLabel,
                $Resources.CreditCardAddedLabel,
                $Resources.CreditCardDefaultLabel,
                $Resources.CreditCardDeleteLabel,
            ]"
            hideToolbar>
            <template #item="{ item: creditCard }">
                <td>
                    <i class="payment-icon" :class="creditCardBrandClass(creditCard)"></i>
                </td>
                <td>{{ creditCard.Mask }}</td>
                <td>{{ formatCustomDatePattern(new Date(creditCard.Expires), 'MM/yy') }}</td>
                <td>{{ creditCard.CardType }}</td>
                <td>{{ creditCard.User.FirstName }} {{ creditCard.User.LastName }}</td>
                <td>{{ formatDate(creditCard.Created) }}</td>
                <td>
                    <span class="strong" v-if="creditCard.IsDefault">{{ $Resources.CreditCardIsDefaultText }}</span>
                    <ConfirmButton
                        v-else-if="canMakeDefault(creditCard)"
                        :buttonText="$Resources.CreditCardMakeDefaultBtnText"
                        buttonClasses="btn-sm"
                        @confirm="makeDefault(creditCard)"
                        undoButton></ConfirmButton>
                </td>
                <td class="text-right">
                    <ConfirmButton
                        v-if="canDelete(creditCard)"
                        :buttonText="$Resources.CreditCardDeleteLabel"
                        buttonClasses="btn-sm"
                        @confirm="remove(creditCard)"
                        undoButton></ConfirmButton>
                </td>
            </template>
        </List>
    </template>
</template>

